import { Trans, useLingui } from '@lingui/react/macro';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import { IconButton, Stack, Typography } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { TelegramIcon, TwitterXIcon } from '../../../components/footer/social_icons.tsx';
import {
  useReferralCardData,
  useReferralLinkCopy,
  useReferralShare,
} from '../../../features/referral/hooks/useReferralCardData.ts';
import { accountMenuGhostIconButtonSx } from './account_menu_styles.ts';

interface AccountMenuReferralProps {
  onNavigate?: () => void;
}

export function AccountMenuReferral({ onNavigate }: AccountMenuReferralProps) {
  const { t } = useLingui();
  const { referralLink, refCode, shouldDisplayData } = useReferralCardData();
  const { copied, handleCopy, isCopyDisabled } = useReferralLinkCopy(referralLink);

  const hasReferral = shouldDisplayData && Boolean(referralLink);
  const remainingInvites = Math.max((refCode?.limit ?? 0) - (refCode?.used ?? 0), 0);
  const actionsDisabled = !hasReferral || remainingInvites === 0;

  const { handleShareTelegram, handleShareTwitter } = useReferralShare(referralLink, actionsDisabled);

  if (!hasReferral) {
    return null;
  }

  return (
    <Stack spacing={0.75} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          <Trans comment="Referral section title in account menu">Referral code</Trans>
        </Typography>
        <Typography
          component={Link}
          to="/referrals"
          onClick={onNavigate}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            fontSize: 11,
            fontWeight: 500,
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Trans comment="Link to referral stats">Details</Trans>
          <ArrowForwardIcon sx={{ fontSize: 11 }} />
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1,
          bgcolor: 'background.paper-elevation-5',
        }}
      >
        <Typography noWrap sx={{ flex: 1, minWidth: 0, px: 0.5, fontSize: 13, fontWeight: 500 }} className="numeric">
          {refCode?.code ?? ''}
        </Typography>
        <IconButton
          size="small"
          sx={accountMenuGhostIconButtonSx}
          onClick={handleCopy}
          disabled={isCopyDisabled || actionsDisabled}
          aria-label={t({ comment: 'Copy referral code', message: 'Copy referral code' })}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          sx={accountMenuGhostIconButtonSx}
          onClick={handleShareTelegram}
          disabled={actionsDisabled}
          aria-label={t({ comment: 'Share referral link on Telegram', message: 'Share on Telegram' })}
        >
          <TelegramIcon />
        </IconButton>
        <IconButton
          size="small"
          sx={accountMenuGhostIconButtonSx}
          onClick={handleShareTwitter}
          disabled={actionsDisabled}
          aria-label={t({ comment: 'Share referral link on X (Twitter)', message: 'Share on X' })}
        >
          <TwitterXIcon />
        </IconButton>
      </Stack>

      {copied ? (
        <Typography sx={{ fontSize: 11, color: 'success.main' }}>
          <Trans comment="Referral code copied confirmation">Copied</Trans>
        </Typography>
      ) : null}
    </Stack>
  );
}
